import {
  Calendar,
  ChevronDown,
  Clock,
  Edit,
  Filter,
  MessageSquare,
  MoreHorizontal,
  Phone,
  Plus,
  Search,
  Trash2,
  BarChart,
} from "lucide-react";
import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import api from "../../api";
import moment from "moment";

const VoiceAgent = () => {
  const navigate = useNavigate();
  const [agents, setAgents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    status: "all",
    dateRange: "all",
  });

  // Brand Colors
  const brandColors = {
    primary: "#575955",
    secondary: "#55C5D0",
  };

  useEffect(() => {
    fetchAgents();
  }, []);

  const fetchAgents = async () => {
    setIsLoading(true);
    try {
      const response = await api.get("api/ai-voice-caller/voice-agents/");
      // Handle both paginated response {results: []} and direct array response
      const agentsData = Array.isArray(response.data)
        ? response.data
        : (response.data.results || []);
      setAgents(agentsData);
    } catch (error) {
      console.error("Error fetching voice agents:", error);
      toast.error("Failed to load voice agents");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateAgent = () => {
    navigate("/campaign-setup");
  };

  const handleEditAgent = (agentId) => {
    // Navigate to edit page or open edit modal
    navigate(`/campaign-setup?agent=${agentId}`);
  };

  const handleDeleteAgent = async (agentId) => {
    if (window.confirm("Are you sure you want to delete this voice agent?")) {
      try {
        await api.delete(`api/ai-voice-caller/voice-agents/${agentId}/`);
        toast.success("Voice agent deleted successfully");
        fetchAgents(); // Refresh the list
      } catch (error) {
        console.error("Error deleting voice agent:", error);
        toast.error("Failed to delete voice agent");
      }
    }
  };

  const handleStartCampaign = (agentId) => {
    // Navigate to AI Call Center with this agent selected
    navigate(`/ai-calls?agent=${agentId}`);
  };

  const filteredAgents = agents.filter((agent) => {
    // Apply search term filter
    if (
      searchTerm &&
      !agent.name.toLowerCase().includes(searchTerm.toLowerCase())
    ) {
      return false;
    }

    // Apply status filter
    if (filters.status !== "all" && agent.status !== filters.status) {
      return false;
    }

    // Apply date filter
    if (filters.dateRange !== "all") {
      const updatedMoment = moment(agent.updated_at);
      const now = moment();

      switch (filters.dateRange) {
        case "today":
          return updatedMoment.isSame(now, "day");
        case "week":
          return updatedMoment.isSameOrAfter(now.subtract(1, "week"), "day");
        case "month":
          return updatedMoment.isSameOrAfter(now.subtract(1, "month"), "day");
        default:
          return true;
      }
    }

    return true;
  });

  // --- SUMMARY STATS HELPERS ---
  // These are mock calculations; replace with real data if available
  const totalAgents = agents.length;
  const activeAgents = agents.filter((a) => a.status === "active").length;
  // For demo: sum calls_made, avg success_rate, avg_duration from agent.stats if available
  const totalCalls = agents.reduce((sum, a) => sum + (a.calls_made || 0), 0);
  const avgSuccessRate = agents.length
    ? (
      agents.reduce((sum, a) => sum + (a.success_rate || 0), 0) /
      agents.length
    ).toFixed(1)
    : 0;

  // --- UI ---
  return (
    <div className="space-y-8">
      {/* Header + Create Button */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl sm:text-3xl font-semibold text-gray-900">Voice Agents</h2>
          <p className="text-sm sm:text-base text-gray-600 mt-1">
            Manage AI-powered voice agents for automated calling campaigns
          </p>
        </div>
        <button
          onClick={handleCreateAgent}
          className="w-full sm:w-auto px-5 py-2.5 rounded-xl text-white flex items-center justify-center space-x-2 text-base font-medium shadow-md hover:scale-105 transition-transform"
          style={{ backgroundColor: brandColors.secondary }}
        >
          <Plus className="w-5 h-5" />
          <span>Create Voice Agent</span>
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6">
        <div className="bg-white rounded-2xl shadow border border-gray-100 p-3 sm:p-6 flex flex-col items-start min-w-0">
          <span className="text-gray-500 text-[10px] sm:text-sm mb-1 truncate w-full">Total Agents</span>
          <span className="text-lg sm:text-2xl font-bold text-gray-900 truncate w-full">
            {totalAgents}
          </span>
        </div>
        <div className="bg-white rounded-2xl shadow border border-gray-100 p-3 sm:p-6 flex flex-col items-start min-w-0">
          <span className="text-gray-500 text-[10px] sm:text-sm mb-1 truncate w-full">Active Agents</span>
          <span className="text-lg sm:text-2xl font-bold text-gray-900 truncate w-full">
            {activeAgents}
          </span>
        </div>
        <div className="bg-white rounded-2xl shadow border border-gray-100 p-3 sm:p-6 flex flex-col items-start min-w-0">
          <span className="text-gray-500 text-[10px] sm:text-sm mb-1 truncate w-full">Total Calls</span>
          <span className="text-lg sm:text-2xl font-bold text-gray-900 truncate w-full">{totalCalls}</span>
        </div>
        <div className="bg-white rounded-2xl shadow border border-gray-100 p-3 sm:p-6 flex flex-col items-start min-w-0">
          <span className="text-gray-500 text-[10px] sm:text-sm mb-1 truncate w-full">Avg Success Rate</span>
          <span className="text-lg sm:text-2xl font-bold text-gray-900 truncate w-full">
            {avgSuccessRate}%
          </span>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="relative flex-1">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            placeholder="Search voice agents..."
            className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="relative">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="px-4 py-2 border border-gray-300 rounded-xl flex items-center space-x-2 hover:bg-gray-50"
          >
            <Filter className="h-5 w-5 text-gray-500" />
            <span>Filters</span>
            <ChevronDown className="h-4 w-4 text-gray-500" />
          </button>

          {showFilters && (
            <div className="absolute right-0 mt-2 w-64 bg-white rounded-xl shadow-lg z-10 p-4 border border-gray-100">
              <div className="space-y-4">
                {/* <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Status
                  </label>
                  <select
                    value={filters.status}
                    onChange={(e) =>
                      setFilters({ ...filters, status: e.target.value })
                    }
                    className="w-full p-2 border border-gray-300 rounded-lg"
                  >
                    <option value="all">All Statuses</option>
                    <option value="active">Active</option>
                    <option value="paused">Paused</option>
                    <option value="completed">Completed</option>
                  </select>
                </div> */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Date Range
                  </label>
                  <select
                    value={filters.dateRange}
                    onChange={(e) =>
                      setFilters({ ...filters, dateRange: e.target.value })
                    }
                    className="w-full p-2 border border-gray-300 rounded-lg"
                  >
                    <option value="all">All Time</option>
                    <option value="today">Today</option>
                    <option value="week">This Week</option>
                    <option value="month">This Month</option>
                  </select>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Voice Agents List */}
      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(3)].map((_, index) => (
            <div
              key={index}
              className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm animate-pulse"
            >
              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 rounded-full bg-gray-200"></div>
                    <div>
                      <div className="h-4 bg-gray-200 rounded w-32 mb-2"></div>
                      <div className="h-3 bg-gray-200 rounded w-24"></div>
                    </div>
                  </div>
                  <div className="h-6 w-6 bg-gray-200 rounded-full"></div>
                </div>

                <div className="space-y-3 mb-6">
                  <div className="h-4 bg-gray-200 rounded w-48"></div>
                  <div className="h-4 bg-gray-200 rounded w-40"></div>
                  <div className="h-4 bg-gray-200 rounded w-52"></div>
                </div>

                <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                  <div className="flex space-x-2">
                    <div className="h-8 w-8 bg-gray-200 rounded-lg"></div>
                    <div className="h-8 w-8 bg-gray-200 rounded-lg"></div>
                  </div>
                  <div className="h-8 w-24 bg-gray-200 rounded-lg"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : filteredAgents.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
          <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
            <Phone className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No voice agents found
          </h3>
          <p className="text-gray-500 mb-6">
            {searchTerm
              ? "No agents match your search criteria"
              : "You haven't created any voice agents yet"}
          </p>
          <button
            onClick={handleCreateAgent}
            className="px-4 py-2 rounded-lg text-white inline-flex items-center space-x-2"
            style={{ backgroundColor: brandColors.secondary }}
          >
            <Plus className="w-5 h-5" />
            <span>Create Your First Agent</span>
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredAgents.map((agent) => (
            <div
              key={agent.id || agent.voice_agent}
              className="bg-white rounded-2xl border border-gray-100 shadow hover:shadow-lg transition-shadow overflow-hidden flex flex-col"
            >
              <div className="p-6 flex-1 flex flex-col">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <h3 className="font-semibold text-lg text-gray-900 mb-1">
                      {agent.name}
                    </h3>
                    <p className="text-sm text-gray-500 mb-1">
                      {agent.description || "AI agent for calling campaigns"}
                    </p>
                    <div className="flex flex-wrap items-center gap-2 mb-3">
                      <span
                        className={`text-[10px] sm:text-xs px-2 py-0.5 rounded-full font-medium ${getStatusStyles(
                          agent.status || "active"
                        )}`}
                      >
                        {agent.status || "Active"}
                      </span>
                      <span className="text-xs text-gray-400 hidden sm:inline">|</span>
                      <span className="text-[10px] sm:text-xs text-gray-700">
                        Voice:{" "}
                        <span className="font-medium text-gray-900">
                          {agent.voice_name || "N/A"}
                        </span>
                      </span>
                      <span className="text-xs text-gray-400 hidden sm:inline">|</span>
                      <span className="text-[10px] sm:text-xs text-gray-700">
                        Lang:{" "}
                        <span className="font-medium text-gray-900">
                          {agent.language || "English (US)"}
                        </span>
                      </span>
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <span
                      className={`text-xs px-2 py-0.5 rounded-full font-medium ${getStatusStyles(
                        agent.status || "active"
                      )}`}
                    >
                      {agent.status || "Active"}
                    </span>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2 text-center my-3">
                  <div>
                    <div className="text-base font-bold text-gray-900">
                      {agent.calls_made || 0}
                    </div>
                    <div className="text-xs text-gray-500">Calls Made</div>
                  </div>
                  <div>
                    <div className="text-base font-bold text-gray-900">
                      {agent.success_rate ? `${agent.success_rate}%` : "0%"}
                    </div>
                    <div className="text-xs text-gray-500">Success Rate</div>
                  </div>
                  <div>
                    <div className="text-base font-bold text-gray-900">
                      {agent.avg_duration || "--"}
                    </div>
                    <div className="text-xs text-gray-500">Avg Duration</div>
                  </div>
                </div>
                <div className="bg-gray-50 rounded-lg p-3 text-xs text-gray-700 mb-4">
                  <span className="font-semibold text-gray-800">
                    Script Preview:
                  </span>{" "}
                  {agent.script_preview || "No script preview available."}
                </div>
                <div className="flex flex-wrap sm:flex-nowrap gap-2 mt-auto">
                  <button
                    onClick={() =>
                      handleEditAgent(agent.id || agent.voice_agent)
                    }
                    className="flex-1 min-w-[120px] px-3 py-2 rounded-lg text-sm font-medium flex items-center justify-center gap-2 border border-gray-200 hover:bg-gray-50"
                  >
                    <Edit className="w-4 h-4" /> Configure
                  </button>
                  <button
                    onClick={() =>
                      handleDeleteAgent(agent.id || agent.voice_agent)
                    }
                    className="px-3 py-2 rounded-lg text-sm font-medium flex items-center justify-center gap-2 text-red-600 border border-gray-200 hover:bg-red-50"
                    title="Delete"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() =>
                      handleStartCampaign(agent.id || agent.voice_agent)
                    }
                    className="flex-1 min-w-[120px] px-3 py-2 rounded-lg text-sm font-medium flex items-center justify-center gap-2 text-white"
                    style={{ backgroundColor: brandColors.secondary }}
                  >
                    <BarChart className="w-4 h-4" /> Analytics
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Helper functions
const getStatusStyles = (status) => {
  switch (status.toLowerCase()) {
    case "active":
      return "bg-green-100 text-green-800";
    case "paused":
      return "bg-yellow-100 text-yellow-800";
    case "completed":
      return "bg-blue-100 text-blue-800";
    default:
      return "bg-gray-100 text-gray-800";
  }
};

const formatDaysAllowed = (daysAllowed) => {
  if (!daysAllowed) return "All days";

  // If it's a string like "monday,tuesday,wednesday"
  if (typeof daysAllowed === "string") {
    const days = daysAllowed.split(",");
    if (days.length === 7) return "All days";
    if (
      days.length === 5 &&
      days.includes("monday") &&
      days.includes("tuesday") &&
      days.includes("wednesday") &&
      days.includes("thursday") &&
      days.includes("friday")
    ) {
      return "Weekdays";
    }
    if (
      days.length === 2 &&
      days.includes("saturday") &&
      days.includes("sunday")
    ) {
      return "Weekends";
    }
    return days.map((d) => d.charAt(0).toUpperCase() + d.slice(1)).join(", ");
  }

  // If it's an object like {Monday: true, Tuesday: true, ...}
  if (typeof daysAllowed === "object") {
    const days = Object.entries(daysAllowed)
      .filter(([_, value]) => value)
      .map(([day]) => day);

    if (days.length === 7) return "All days";
    if (
      days.length === 5 &&
      days.includes("Monday") &&
      days.includes("Tuesday") &&
      days.includes("Wednesday") &&
      days.includes("Thursday") &&
      days.includes("Friday")
    ) {
      return "Weekdays";
    }
    if (
      days.length === 2 &&
      days.includes("Saturday") &&
      days.includes("Sunday")
    ) {
      return "Weekends";
    }
    return days.join(", ");
  }

  return "All days";
};

const formatTime = (timeString) => {
  if (!timeString) return "";

  // Handle different time formats
  let hours, minutes;

  if (timeString.includes(":")) {
    [hours, minutes] = timeString.split(":");
  } else if (timeString.length === 4) {
    hours = timeString.substring(0, 2);
    minutes = timeString.substring(2, 4);
  } else {
    return timeString; // Return as is if format is unknown
  }

  hours = parseInt(hours);
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12 || 12; // Convert to 12-hour format

  return `${hours}:${minutes} ${ampm}`;
};

export default VoiceAgent;
